import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useOverlays } from "contexts/OverlayContext";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { PlusIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    TransactionCategoryRow,
    TransactionCategoryRowType,
} from "../viewUtils";

export const ActionsCell = memo(function ActionsCell({
    row,
}: CellContext<ViewTableFeatures, TransactionCategoryRow, unknown>) {
    const item = row.original;

    if (item.type === TransactionCategoryRowType.Custom) {
        return <CustomCategoryActions item={item} />;
    }

    return <AddSubcategoryIndicator item={item} />;
});

// ─── Custom category: update + delete ────────────────────────────────────────

const CustomCategoryActions = ({
    item,
}: Readonly<{ item: TransactionCategoryRow }>) => {
    const { openOverlay } = useOverlays();

    return (
        <ViewTableCell captureClicks>
            <Menu.More className={viewTableStyles.showOnHover}>
                <Menu.Item
                    onClick={() =>
                        openOverlay("updateCategory", {
                            category: item.category,
                        })
                    }
                >
                    Update
                </Menu.Item>
                <Menu.Item
                    onClick={() =>
                        openOverlay("deleteCategory", {
                            category: item.category,
                            label: item.label,
                            transactionsCount: item.transactionsCount,
                        })
                    }
                    destructive
                >
                    Delete
                </Menu.Item>
            </Menu.More>
        </ViewTableCell>
    );
};

// ─── Non-custom category: add subcategory button ──────────────────────────────

const AddSubcategoryIndicator = ({
    item,
}: Readonly<{ item: TransactionCategoryRow }>) => {
    const { openOverlay } = useOverlays();

    if (item.parentId !== null || item.disableAddChildren) {
        return null;
    }

    return (
        <ViewTableCell captureClicks>
            <Tooltip content="Add subcategory">
                <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    aria-label="Add subcategory"
                    onClick={(e) => {
                        e.stopPropagation();
                        openOverlay("createCategory", {
                            parentCategoryId: item.id,
                        });
                    }}
                >
                    <PlusIcon />
                </Button>
            </Tooltip>
        </ViewTableCell>
    );
};
